import { useCallback, useEffect, useState } from "react";

import { useT } from "../../../shared/i18n";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  controlClassName,
  ListRow,
  Skeleton,
} from "../../../shared/ui";
import {
  InspectorDenied,
  type InspectorApi,
  type JobDetail,
  type JobFilters,
  type JobSummary,
} from "./inspectorApi";
import { ErrorRetry } from "../ErrorRetry";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { JobTrace } from "./JobTrace";
import { RedactedText } from "./RedactedText";

const STATES = ["succeeded", "failed", "running", "cancelled", "expired"];

type Status = "loading" | "ready" | "failed" | "denied";

function summaryText(job: JobSummary): string {
  return [
    job.intent ?? "-",
    job.outcome_kind ?? "-",
    `${String(job.corrections)} corr`,
    `${String(job.tokens_in)}/${String(job.tokens_out)} tok`,
    job.feedback ?? "",
  ]
    .filter((part) => part !== "")
    .join(" | ");
}

function metaText(job: JobSummary): string {
  return [
    job.outcome_kind ?? "-",
    `${String(job.corrections)} corr`,
    `${String(job.tokens_in)}/${String(job.tokens_out)} tok`,
    job.feedback ?? "",
    new Date(job.created_at).toLocaleString(),
  ]
    .filter((part) => part !== "")
    .join(" | ");
}

/**
 * Operator list of assistant jobs and their decision timeline (ADR 0217).
 * Without `assistant.trace.read` it explains the missing permission.
 */
export function JobInspectorPanel({ api }: { api: InspectorApi }) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [filters, setFilters] = useState<JobFilters>({});
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);

  const apply = useCallback(
    (
      request: Promise<{ items: JobSummary[]; next_cursor: string | null }>,
      after: string | null,
    ) => {
      request
        .then((page) => {
          setJobs((current) =>
            after === null ? page.items : [...current, ...page.items],
          );
          setCursor(page.next_cursor);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (isSessionExpired(error)) reportExpired();
          setStatus(error instanceof InspectorDenied ? "denied" : "failed");
        });
    },
    [reportExpired],
  );

  useEffect(() => {
    apply(api.listJobs({}, null), null);
  }, [api, apply]);

  const load = (next: JobFilters, after: string | null) => {
    setStatus("loading");
    apply(api.listJobs(next, after), after);
  };

  const refilter = (next: JobFilters) => {
    setFilters(next);
    load(next, null);
  };

  const open = async (jobId: string) => {
    setDetailFailed(false);
    try {
      setDetail(await api.jobDetail(jobId));
    } catch (error: unknown) {
      if (isSessionExpired(error)) reportExpired();
      setDetailFailed(true);
    }
  };

  if (status === "denied") {
    return (
      <section
        aria-label={t("admin.inspector.title")}
        className="flex flex-col gap-3"
      >
        <h2 className="text-title text-fg">{t("admin.inspector.title")}</h2>
        <PermissionDenied message={t("admin.denied.inspector")} />
      </section>
    );
  }

  if (detail !== null) {
    return (
      <section
        aria-label={t("admin.inspector.title")}
        className="flex flex-col gap-3"
      >
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDetail(null);
            }}
          >
            {t("admin.inspector.back")}
          </Button>
        </div>
        <Card title={summaryText(detail)}>
          <div className="flex flex-wrap gap-2">
            <Badge tone={detail.state === "succeeded" ? "success" : "warning"}>
              {detail.state}
            </Badge>
            <Badge tone="neutral">{detail.prompt_version ?? "-"}</Badge>
            {detail.trace?.failure_code ? (
              <Badge tone="error">{detail.trace.failure_code}</Badge>
            ) : null}
          </div>
        </Card>
        {detail.trace === null ? (
          <Banner tone="info">{t("admin.inspector.noTrace")}</Banner>
        ) : (
          <JobTrace turns={detail.trace.turns} />
        )}
        {detail.snapshot !== null &&
        typeof detail.snapshot.owner_text === "string" ? (
          <Card title={t("admin.inspector.snapshot")}>
            <RedactedText text={detail.snapshot.owner_text} />
            {typeof detail.snapshot.reply === "string" && (
              <RedactedText text={detail.snapshot.reply} />
            )}
          </Card>
        ) : null}
      </section>
    );
  }

  return (
    <section
      aria-label={t("admin.inspector.title")}
      className="flex flex-col gap-3"
    >
      <h2 className="text-title text-fg">{t("admin.inspector.title")}</h2>
      <Banner tone="info">{t("admin.inspector.notice")}</Banner>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <label className="flex min-w-40 flex-1 flex-col gap-1 text-footnote text-fg-muted">
          {t("admin.inspector.filter.state")}
          <select
            className={controlClassName}
            value={filters.state ?? ""}
            onChange={(event) => {
              refilter({
                ...filters,
                state: event.target.value || undefined,
              });
            }}
          >
            <option value="">{t("admin.inspector.filter.anyState")}</option>
            {STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </label>
        <div className="flex w-full flex-col">
          <Checkbox
            label={t("admin.inspector.filter.correction")}
            checked={filters.hasCorrection === true}
            onCheckedChange={(checked) => {
              refilter({
                ...filters,
                hasCorrection: checked ? true : undefined,
              });
            }}
          />
          <Checkbox
            label={t("admin.inspector.filter.feedback")}
            checked={filters.hasFeedback === true}
            onCheckedChange={(checked) => {
              refilter({ ...filters, hasFeedback: checked ? true : undefined });
            }}
          />
        </div>
      </div>
      {status === "failed" && (
        <ErrorRetry
          message={t("admin.inspector.failed")}
          onRetry={() => {
            load(filters, null);
          }}
        />
      )}
      {detailFailed && (
        <Banner tone="error">{t("admin.inspector.failed")}</Banner>
      )}
      {status === "loading" && jobs.length === 0 && (
        <div aria-busy="true">
          <Skeleton height={48} />
          <span className="sr-only">{t("admin.inspector.loading")}</span>
        </div>
      )}
      {status === "ready" && jobs.length === 0 && (
        <p className="text-body text-fg-muted">{t("admin.inspector.empty")}</p>
      )}
      {jobs.length > 0 && (
        <Card flush>
          <ul>
            {jobs.map((job) => (
              <li key={job.job_id}>
                <ListRow
                  title={`${job.state} · ${job.intent ?? "-"}`}
                  subtitle={metaText(job)}
                  trailing={
                    <Button
                      variant="tinted"
                      size="sm"
                      onClick={() => {
                        void open(job.job_id);
                      }}
                    >
                      {t("admin.inspector.open")}
                    </Button>
                  }
                />
              </li>
            ))}
          </ul>
        </Card>
      )}
      {cursor !== null && (
        <Button
          variant="secondary"
          onClick={() => {
            load(filters, cursor);
          }}
        >
          {t("admin.inspector.more")}
        </Button>
      )}
    </section>
  );
}
